/***********************************************
* PhotomationCore.js
* 
* Handles the complete application view
***********************************************/
/* Layout files */
import '../assets/style/Photomation.css'

/* Global imports */
import React, { useState, useEffect }   from 'react';
import { withTranslation } from 'react-i18next';
import { clsx } from 'clsx';

/* Local imports */
import API              from './API';
import { ProjectPopup } from './Popup';
import Project          from '../pages/Project';
import SideBar          from './SideBar';
import Tools            from './tools/Tools';
import PhotoTool        from './PhotoTool';

function PotreeViewer(props) {
    const [viewerId, _] = useState(Math.round(Math.random() * 10000000));

    return (<div key="outputContainer" className="outputContainer">
        {props.showViewer ? <iframe src={"/viewer/?viewerId=" + viewerId + "&project_id=" + props.id} title="3D Viewer" className='outputViewer'></iframe> : <></>}
    </div>);
}

function PhotomationCore(props) {
    const [projectName, setProjectName] = useState("");
    const [projectId, setProjectId] = useState(0);
    const [prices, setPrices] = useState({base: 0.0, pc: 0.0, mesh: 0.0, autocad: 0.0, ortho: 0.0});
    const [epsg, setEpsg] = useState([]);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [editProject, setEditProject] = useState(false);
    const [uploadDone, setUploadDone] = useState(false);
    const [imageCount, setImageCount] = useState(0);
    const [activeTab, setActiveTab] = useState(0);
    const [hasOutput, setHasOutput] = useState(false);
    const [viewerLoaded, setViewerLoaded] = useState(false);
    const [pickedPoint, setPickedPoint] = useState({x: 0.0, y: 0.0, z: 0.0})

    useEffect(() => {
        API.GetPrices(handlePrices);
        API.GetEpsg(handleEpsg);
    }, []);

    useEffect(() => {
        const handleMessage = (event) => {
            if (event.data.type === 'potree_point_picked') {
                setPickedPoint({x: event.data.x, y: event.data.y, z: event.data.z});
            }
        };
    
        window.addEventListener("message", handleMessage);
    
        return () => window.removeEventListener("message", handleMessage);
    }, []);

    useEffect(() => {
        if(pickedPoint.x !== 0.0 && pickedPoint.y !== 0.0 && pickedPoint.z !== 0.0) {
            setActiveTab(2);
        }
    }, [pickedPoint])

    /*useEffect(() => {
        if(activeTab === 1) {
            setViewerLoaded(true);
        }
    }, [activeTab]);*/

    useEffect(() => {
        setViewerLoaded(false);
        if(!notTabletOrPhone) { setSidebarOpen(false); } 
        setActiveTab(0);
    }, [projectId]);

    const handlePrices = (reply) => { setPrices(reply[0]); }
    const handleEpsg   = (reply) => { setEpsg(reply);      }

    let ret = []
    let notTabletOrPhone = (!props.tablet && !props.phone);
    //let tabletOrPhone = props.tablet || props.phone;
    let tabletOrPhone = true;
    let sbOpen = sidebarOpen; // || notTabletOrPhone;

    /* Title bar */
    if(projectName !== "") {
        let title = (
        <div className="projectname" key="projectname">
            <h1 key="title">{projectName}</h1>
            <button onClick={() => setEditProject(true)}><span className="material-symbols-outlined">edit</span></button>
            <ProjectPopup 
                title={props.t("changeprojname")}
                btnText={props.t("save")}
                text={projectName}
                open={editProject} 
                onClose={() => setEditProject(false)} 
                onSave={(name) => {setProjectName(name); setEditProject(false); API.ChangeProjectName(projectId, name);}}
            />
        </div>);
        ret.splice(0, 0, title);
    }
        
    /* Project List */
    if(tabletOrPhone && (projectId !== 0)) { // If on a tablet or phone, show hamburger button
        ret.push(
        <div key="sidebar-hamburger" className="sbHamburger" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <div className={clsx("sbHambBar1", sbOpen && "sbBarOpen")}></div>
            <div className={clsx("sbHambBar2", sbOpen && "sbBarOpen")}></div>
            <div className={clsx("sbHambBar3", sbOpen && "sbBarOpen")}></div>
        </div>);
    }
    ret.push(<SideBar t={props.t} open={sbOpen} key="sidebar" onProjectChanged={
        (pId, pName) => {
            setProjectId(pId); 
            setProjectName(pName);
        }
    }/>);

    const tabConfig = [
        { id: 0, label: "create", requiresOutput: false },
        { id: 1, label: "viewer", requiresOutput: true },
        { id: 2, label: "photos", requiresOutput: true },
        { id: 3, label: "tools",  requiresOutput: true },
    ];


    const tabViews = [
        { id: 0, Component: Project,
          props: {
            t: props.t,
            projectId,
            prices,
            epsg,
            tablet: props.tablet,
            phone: props.phone,
            uploadDone,
            imageCount,
            hasOutput: (o) => setHasOutput(o),
          },
        },
        { id: 1, Component: PotreeViewer, props: { id: projectId, showViewer: activeTab === 1 || viewerLoaded, }, },
        { id: 2, Component: PhotoTool, className: "dontstretch", props: { id: projectId, pickedPoint, name: projectName, t: props.t, }, },
        { id: 3, Component: Tools, className: "dontstretch", props: { id: projectId, }, },
    ];

    ret.push(
    <div key="tabbedView" className={clsx("tabbedView", (sbOpen && notTabletOrPhone) && 'sbOpen', (projectId === 0) && 'hidden')}>
        <div className='tabs'>
            {tabConfig.map((tab) => {
                const isDisabled = tab.requiresOutput && !hasOutput;
                return (
                <div 
                    key={tab.label} 
                    className={clsx("tab", activeTab === tab.id && "active", isDisabled && "disabled" )} 
                    onClick={() => { if(!isDisabled) setActiveTab(tab.id); }}>
                    {props.t(tab.label)}
                </div>
                );
            })}
        </div>

        {tabViews.map(({ id, Component, props: componentProps, className }) => (
            <div 
                key={`view-${id}`} 
                className={clsx("tabView", className, activeTab === id && "active")}
            >
                <Component {...componentProps} />
            </div>
        ))}
    </div>);

    return ret;
}

export default withTranslation("Projects")(PhotomationCore);